import { supabase } from "./supabase";
import { formatCompte8, formatNumeroCompteCollectifFournisseur } from "./comptable/formatCompte";
import { regimeTvaFactureAchatDepuisFournisseur, type RegimeTvaFactureAchat } from "./factureAchatTva";

export type TvaExigibiliteFournisseur = "encaissements" | "debits";

/** Régime fiscal (conformité TVA). */
export type RegimeFiscalFournisseur =
  | "reel_normal"
  | "reel_simplifie"
  | "micro"
  | "tee"
  | "inconnu";

export const REGIME_FISCAL_OPTIONS: { value: RegimeFiscalFournisseur; label: string }[] = [
  { value: "reel_normal", label: "Réel normal" },
  { value: "reel_simplifie", label: "Réel simplifié" },
  { value: "micro", label: "Microentreprise" },
  { value: "tee", label: "TEE" },
  { value: "inconnu", label: "Inconnu" },
];

export function libelleRegimeFiscalPourMessage(r: RegimeFiscalFournisseur | null | undefined): string {
  const map: Record<RegimeFiscalFournisseur, string> = {
    reel_normal: "réel normal",
    reel_simplifie: "réel simplifié",
    micro: "microentreprise",
    tee: "TEE",
    inconnu: "inconnu",
  };
  return r && map[r] ? map[r] : "inconnu";
}

/** Résultat explicite de createFournisseur pour distinguer création vs doublon détecté. */
export type CreateFournisseurResult =
  | { status: "created"; supplier: Fournisseur }
  | { status: "duplicate_found"; supplier: Fournisseur };

export interface Fournisseur {
  id: string;
  dossier_id: string;
  nom: string;
  ncc?: string | null;
  numero_compte?: string | null;
  compte_auxiliaire_id?: string | null;
  adresse?: string | null;
  code_postal?: string | null;
  ville?: string | null;
  pays?: string | null;
  telephone?: string | null;
  email?: string | null;
  iban?: string | null;
  numero_tva?: string | null;
  siret?: string | null;
  profession?: string | null;
  /** Compte de charge OHADA (contrepartie) */
  compte_charge_numero?: string | null;
  /** Taux TVA par défaut (%) */
  taux_tva?: number | null;
  /** TVA sur encaissements ou sur débits */
  tva_exigibilite?: TvaExigibiliteFournisseur | null;
  /** Solde affiché (FCFA) */
  solde_fcfa?: number | null;
  /** Délai de paiement (ex: reception, 15j, 30j) */
  delai_paiement?: string | null;
  /** Moyen de paiement préféré */
  moyen_paiement?: string | null;
  /** Régime fiscal (TVA) */
  regime_fiscal?: RegimeFiscalFournisseur | null;
  created_at?: string;
  updated_at?: string;
}

export async function fetchFournisseurs(dossierId: string): Promise<Fournisseur[]> {
  const { data, error } = await supabase
    .from("fournisseurs")
    .select("*")
    .eq("dossier_id", dossierId)
    .order("nom");
  if (error) throw error;
  return data || [];
}

// ─── Anti-doublon fournisseur (normalisation prudente) ───────────────────

/** Normalisation principale : trim, lowercase, espaces multiples. */
function normalizeNom(nom: string): string {
  return (nom || "").trim().toLowerCase().replace(/\s+/g, " ");
}

/** Comparaison secondaire : retire les préfixes juridiques courants pour rapprochement. */
function nomSansPrefixeJuridique(nom: string): string {
  return (nom || "")
    .replace(/^(sarl|sas|sa|société|societe|si|eurl|ei|sasu|sarlu)\s+/i, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizeNcc(ncc: string): string {
  return (ncc || "").trim().replace(/\s+/g, "").toLowerCase();
}

function normalizeTelephone(tel: string): string {
  return (tel || "").replace(/\D/g, "");
}

function normalizeEmail(email: string): string {
  return (email || "").trim().toLowerCase();
}

/**
 * Cherche un fournisseur existant probable (doublon) dans le dossier.
 * Priorité : NCC > nom exact > nom sans préfixe juridique (secondaire) > email > téléphone.
 * Toute création fournisseur doit passer par createFournisseur qui utilise cette logique.
 */
export async function findProbableDoublonFournisseur(
  dossierId: string,
  input: { nom: string; ncc?: string | null; email?: string | null; telephone?: string | null }
): Promise<Fournisseur | null> {
  const existing = await fetchFournisseurs(dossierId);
  if (existing.length === 0) return null;

  const nomNorm = normalizeNom(input.nom);
  const nccNorm = input.ncc ? normalizeNcc(input.ncc) : "";
  const emailNorm = input.email ? normalizeEmail(input.email) : "";
  const telNorm = input.telephone ? normalizeTelephone(input.telephone) : "";

  if (!nomNorm && !nccNorm) return null;

  // 1. NCC fourni → priorité haute
  if (nccNorm.length >= 4) {
    const byNcc = existing.find((f) => f.ncc && normalizeNcc(f.ncc) === nccNorm);
    if (byNcc) return byNcc;
  }

  // 2. Nom exact (normalisé)
  const byNomExact = existing.find((f) => normalizeNom(f.nom) === nomNorm);
  if (byNomExact) return byNomExact;

  // 3. Nom sans préfixe juridique (secondaire, prudent)
  if (nomNorm.length >= 3) {
    const nomSansPrefixe = nomSansPrefixeJuridique(input.nom);
    if (nomSansPrefixe) {
      const bySansPrefixe = existing.find(
        (f) => nomSansPrefixeJuridique(f.nom) === nomSansPrefixe
      );
      if (bySansPrefixe) return bySansPrefixe;
    }
  }

  // 4. Email fourni
  if (emailNorm.length >= 5) {
    const byEmail = existing.find((f) => f.email && normalizeEmail(f.email) === emailNorm);
    if (byEmail) return byEmail;
  }

  // 5. Téléphone fourni (au moins 8 chiffres)
  if (telNorm.length >= 8) {
    const byTel = existing.find(
      (f) => f.telephone && normalizeTelephone(f.telephone) === telNorm
    );
    if (byTel) return byTel;
  }

  return null;
}

/** Noms génériques sur une facture : ne pas tenter de matcher un fournisseur. */
function isNomFacturePlaceholder(nom: string | null | undefined): boolean {
  const raw = (nom || "").trim();
  if (raw.length < 2) return true;
  const n = normalizeNom(raw);
  if (n === "à compléter" || n === "a completer") return true;
  if (/^(inconnu|n\/a|na|tbd|\?|\.{2,}|—|-)$/.test(n)) return true;
  return false;
}

function escapeIlikePattern(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

/** Valeur par défaut de `factures_achat.regime_tva` à partir de la fiche fournisseur. */
export async function fetchRegimeTvaFactureDepuisFournisseurFk(
  fournisseurId: string | null | undefined
): Promise<RegimeTvaFactureAchat> {
  if (!fournisseurId) return "debits";
  const { data, error } = await supabase
    .from("fournisseurs")
    .select("tva_exigibilite")
    .eq("id", fournisseurId)
    .maybeSingle();
  if (error || !data) return "debits";
  return regimeTvaFactureAchatDepuisFournisseur((data as { tva_exigibilite?: string | null }).tva_exigibilite);
}

/**
 * Associe une facture d'achat à un fournisseur du dossier : NCC, nom exact / sans préfixe juridique,
 * inclusion prudente (chaîne plus longue ≥ 4 car.), puis ILIKE sur le premier token significatif.
 * Retourne `null` si aucune correspondance claire (création fournisseur possible côté UI).
 */
export async function resolveFournisseurFkPourFactureAchat(
  dossierId: string,
  input: { nom: string | null | undefined; ncc?: string | null }
): Promise<string | null> {
  const nom = (input.nom || "").trim();
  if (isNomFacturePlaceholder(nom)) return null;

  const fromDoublon = await findProbableDoublonFournisseur(dossierId, {
    nom,
    ncc: input.ncc ?? null,
    email: null,
    telephone: null,
  });
  if (fromDoublon) return fromDoublon.id;

  const existing = await fetchFournisseurs(dossierId);
  const nomNorm = normalizeNom(nom);
  const containsMatches = existing.filter((f) => {
    const fn = normalizeNom(f.nom);
    if (!fn || fn.length < 3 || nomNorm.length < 3) return false;
    if (fn === nomNorm) return true;
    const shorter = nomNorm.length <= fn.length ? nomNorm : fn;
    const longer = nomNorm.length > fn.length ? nomNorm : fn;
    if (shorter.length < 4) return false;
    return longer.includes(shorter);
  });

  if (containsMatches.length === 1) return containsMatches[0].id;

  if (containsMatches.length > 1) {
    const scored = containsMatches
      .map((f) => {
        const fn = normalizeNom(f.nom);
        const score =
          fn.includes(nomNorm) || nomNorm.includes(fn) ? Math.min(fn.length, nomNorm.length) : 0;
        return { f, score };
      })
      .sort((a, b) => b.score - a.score);
    if (scored[0].score >= 4 && scored[1].score < scored[0].score) return scored[0].f.id;
  }

  const tokens = nomNorm
    .split(/\s+/)
    .filter((t) => t.length >= 3)
    .sort((a, b) => b.length - a.length);
  for (const token of tokens) {
    const pat = `%${escapeIlikePattern(token)}%`;
    const { data, error } = await supabase
      .from("fournisseurs")
      .select("id, nom")
      .eq("dossier_id", dossierId)
      .ilike("nom", pat);
    if (error) continue;
    const rows = data || [];
    if (rows.length === 1) return (rows[0] as { id: string }).id;
  }

  return null;
}

/**
 * Crée un fournisseur ou retourne l'existant si doublon probable détecté.
 * Tous les chemins de création (page Fournisseurs, future création depuis facture, etc.)
 * doivent utiliser cette fonction.
 */
export async function createFournisseur(
  dossierId: string,
  input: Partial<Fournisseur> & { nom: string }
): Promise<CreateFournisseurResult> {
  const doublon = await findProbableDoublonFournisseur(dossierId, {
    nom: input.nom,
    ncc: input.ncc,
    email: input.email,
    telephone: input.telephone,
  });
  if (doublon) {
    return { status: "duplicate_found", supplier: doublon };
  }

  const rawNumero = (input.numero_compte ?? "").trim();
  const numeroCollectif = rawNumero
    ? formatNumeroCompteCollectifFournisseur(rawNumero)
    : "40100000";

  const { data, error } = await supabase
    .from("fournisseurs")
    .insert({
      dossier_id: dossierId,
      nom: input.nom.trim(),
      ncc: input.ncc || null,
      numero_compte: numeroCollectif,
      adresse: input.adresse || null,
      code_postal: input.code_postal || null,
      ville: input.ville || null,
      pays: input.pays || "CI",
      telephone: input.telephone || null,
      email: input.email || null,
      iban: input.iban || null,
      profession: input.profession || null,
      compte_charge_numero: input.compte_charge_numero?.trim()
        ? formatCompte8(input.compte_charge_numero.trim()) || null
        : null,
      taux_tva: input.taux_tva != null ? Number(input.taux_tva) : null,
      tva_exigibilite: input.tva_exigibilite ?? null,
      solde_fcfa: input.solde_fcfa != null ? Number(input.solde_fcfa) : 0,
      delai_paiement: input.delai_paiement || null,
      moyen_paiement: input.moyen_paiement || null,
      regime_fiscal: input.regime_fiscal ?? "inconnu",
    })
    .select()
    .single();
  if (error) throw error;
  return { status: "created", supplier: data };
}

export async function updateFournisseur(
  id: string,
  input: Partial<Fournisseur>
): Promise<Fournisseur> {
  const payload: Record<string, unknown> = {};
  if (input.nom !== undefined) payload.nom = input.nom.trim();
  if (input.ncc !== undefined) payload.ncc = input.ncc || null;
  if (input.adresse !== undefined) payload.adresse = input.adresse || null;
  if (input.code_postal !== undefined) payload.code_postal = input.code_postal || null;
  if (input.ville !== undefined) payload.ville = input.ville || null;
  if (input.pays !== undefined) payload.pays = input.pays || null;
  if (input.telephone !== undefined) payload.telephone = input.telephone || null;
  if (input.email !== undefined) payload.email = input.email || null;
  if (input.iban !== undefined) payload.iban = input.iban || null;
  if (input.profession !== undefined) payload.profession = input.profession || null;
  if (input.delai_paiement !== undefined) payload.delai_paiement = input.delai_paiement || null;
  if (input.moyen_paiement !== undefined) payload.moyen_paiement = input.moyen_paiement || null;
  if (input.numero_compte !== undefined) {
    const t = (input.numero_compte ?? "").trim();
    payload.numero_compte = t ? formatNumeroCompteCollectifFournisseur(t) : null;
  }
  if (input.compte_charge_numero !== undefined) {
    const c = (input.compte_charge_numero ?? "").trim();
    payload.compte_charge_numero = c ? formatCompte8(c) || null : null;
  }
  if (input.taux_tva !== undefined) payload.taux_tva = input.taux_tva != null ? Number(input.taux_tva) : null;
  if (input.tva_exigibilite !== undefined) payload.tva_exigibilite = input.tva_exigibilite ?? null;
  if (input.solde_fcfa !== undefined) payload.solde_fcfa = input.solde_fcfa != null ? Number(input.solde_fcfa) : 0;
  if (input.regime_fiscal !== undefined) payload.regime_fiscal = input.regime_fiscal ?? "inconnu";

  const { data, error } = await supabase
    .from("fournisseurs")
    .update(payload)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteFournisseur(id: string): Promise<void> {
  const { error } = await supabase.from("fournisseurs").delete().eq("id", id);
  if (error) throw error;
}
